import React from 'react';
import { useFormContext, ValidationOptions } from 'react-hook-form';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { validateRequired } from 'utils/form';

interface TextAreaFieldProps {
	name: string;
	placeholder?: string;
	label: string;
	id: string;
	required?: true;
	wrapperClass?: string;
	error?: string;
	hintText?: string;
	onBlur?: (name: string, value: string | number) => void;
	validation?: ValidationOptions;
	onFocus?: () => void;
	maxLength?: number;
	textInBox?: string;
}

export const TextAreaField = ({
	name,
	label,
	id,
	required,
	wrapperClass = '',
	error,
	hintText,
	onBlur,
	placeholder,
	validation,
	onFocus,
	maxLength = 255,
	textInBox,
}: TextAreaFieldProps) => {
	const { t } = useTranslation('errors');
	const { register, errors } = useFormContext();

	function handleBlur({
		target: { value },
	}: React.FocusEvent<HTMLTextAreaElement>) {
		if (onBlur) {
			onBlur(name, value);
		}
	}

	function parseError(n: string) {
		if (n.includes('.')) {
			const fields = n.split('.');
			const res = fields.reduce((r, field) => {
				return r[field] || r;
			}, errors);
			return res.message;
		}
		return errors?.[n]?.message || errors?.[n]?.types.message;
	}

	return (
		<div
			className={classNames('group', wrapperClass, {
				error: parseError(name),
			})}
		>
			<textarea
				className="group__input"
				name={name}
				id={id}
				placeholder={placeholder || ' '}
				maxLength={maxLength}
				ref={register({
					...validateRequired(required, t('required')),
					...validation,
				})}
				onBlur={handleBlur}
				onFocus={onFocus}
			>
				{textInBox || ''}
			</textarea>
			<div
				className="group__label-bg"
				data-label={required && label ? `${label}*` : label}
			/>
			<label className="group__label" htmlFor={id}>
				{required && label ? `${label}*` : label}
			</label>
			<p
				className={`group__hint font-caption ${
					parseError(name) ? 'group__hint--error' : ''
				}`}
			>
				{parseError(name) || hintText}
			</p>
		</div>
	);
};
